import React, { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import {
  AlertCircle, AlertTriangle, CalendarCheck, CheckCircle2, ChevronRight, FileSpreadsheet, Filter, RefreshCw,
  Search, Thermometer, X,
} from 'lucide-react';
import { useSiteContext } from '../../SiteContext';
import { Page } from '../../interfaces/PagesInterface';
import { exportSickHours, getSickHours, sickHoursErrorMessage, SickHoursPayload } from '../../api/sickHours';
import {
  ChipFit, ChipKey, ColumnFilters, DEFAULT_SORT, DOW, FilterCol, RATE_STATES, SCOPES, SORT_LABEL, STAGES, STAGE_TONE,
  Scope, ShDay, ShRow, SortKey, SortState, StageLabel, RateState, NO_SUPER,
  bucketForStage, clearFilter, columnCounts, dayLabel, deriveRow, emptyFilters, exportFileName, exportSuffix,
  filterChips, fitDayChips, fmtWeekRange, hShort, hrs, inScope, isColumnFiltered, matchesFilters, matchesSearch,
  mdShort, money, nextSort, plural, scopeCounts, sortRows, splitName, summarize, supervisorOptions, weekDates,
} from './sickHoursHelpers';
import { MdPopover, MdTh, MdTip, downloadBlob, useHideOnScroll, useMdToast, usePopoverDismiss } from './mdShared';
import './ModifiedDutyPage.css';
import './SickHoursPage.css';

/**
 * Sick Hours — v2 page (Timesheets › Sick Hours). WCDP payroll only
 * (config.showSickHoursPage + payrollApproveTimesheets).
 *
 * Built from docs/featureBriefs/design_handoff_sick_hours_page_design (README is the
 * spec; its "Resolved" section overrides the rest). Read-only: everyone with an
 * approved or pending sick TOR with hours in the selected week, so payroll knows whose
 * 13-week sick rate to calculate. Rates are still entered on the timesheet.
 *
 * The week is the shell's global week pill (context payPeriodID) — no picker here.
 * Changing it reloads and keeps search, filters and sort. Everything else is
 * client-side over the one GET /admin/timeOff/sickHours payload; Export posts the
 * on-screen employee ids in display order to POST /admin/timeOff/sickHours/export.
 * Row click / Enter hands the employee to the Timesheet Review board as a
 * ReviewIntent, which opens their review console for the same week.
 *
 * Reuses the Modified Duty table, header popovers, chips, search, export button and
 * toast (ModifiedDutyPage.css + mdShared). No layout shift: fixed row height, day
 * chips fold into +n instead of wrapping (fitted on render/resize only), fixed-height
 * summary strip and chip row, reserved export/count widths, overlays are fixed.
 */

interface ColDef {
  key: SortKey | 'go';
  label: string;
  full?: string;
  width: string;
  num?: boolean;
  filter?: FilterCol;
  /** Under the "Sick hours" group header. */
  grp?: boolean;
}

const COLS: ColDef[] = [
  { key: 'emp', label: 'Employee', width: 'auto', filter: 'emp' },
  { key: 'sup', label: 'Superintendent', width: '172px', filter: 'sup' },
  { key: 'days', label: 'Sick days', width: '236px', filter: 'days' },
  { key: 'appr', label: 'Approved', full: 'Approved hrs', width: '108px', num: true, grp: true },
  { key: 'pend', label: 'Pending', full: 'Pending hrs', width: '96px', num: true, grp: true },
  { key: 'total', label: 'Total', full: 'Total hrs', width: '80px', num: true, grp: true },
  { key: 'rate', label: 'Sick rate', width: '128px', num: true, filter: 'rate' },
  { key: 'ts', label: 'Timesheet', width: '168px', filter: 'ts' },
  { key: 'go', label: '', width: '32px' },
];

const POP_TITLE: Record<FilterCol, string> = {
  emp: 'Employee', sup: 'Superintendent', days: 'Sick days', rate: 'Sick rate', ts: 'Timesheet status',
};

const SKELETON_ROWS = 10;
/** [main bar %, sub-line %] per data column, as the prototype's skeleton. */
const SKELETON_W: [number, number][] = [[62, 40], [54, 0], [70, 0], [30, 0], [30, 0], [30, 0], [44, 0], [64, 0]];

/* ====================================================================== *
 * Filter popover
 * ====================================================================== */

type Counts = ReturnType<typeof columnCounts>;

const ShFilterPopover: React.FC<{
  col: FilterCol;
  anchor: HTMLElement;
  filters: ColumnFilters;
  setFilters: React.Dispatch<React.SetStateAction<ColumnFilters>>;
  supOptions: string[];
  counts: Counts;
  /** The week's seven dates (YYYY-MM-DD), or null before the first load. */
  dates: string[] | null;
  onClose: () => void;
}> = ({ col, anchor, filters: f, setFilters, supOptions, counts, dates, onClose }) => {
  const set = <K extends keyof ColumnFilters>(k: K, v: ColumnFilters[K]) => setFilters((prev) => ({ ...prev, [k]: v }));

  /** Toggle one value, keeping the option list's order. */
  function toggle<T extends string | number>(k: 'sup' | 'days' | 'rate' | 'ts', order: T[], v: T, on: boolean) {
    setFilters((prev) => {
      const s = new Set<T>(prev[k] as T[]);
      if (on) s.add(v); else s.delete(v);
      return { ...prev, [k]: order.filter((x) => s.has(x)) };
    });
  }

  function checks<T extends string | number>(
    k: 'sup' | 'days' | 'rate' | 'ts',
    order: T[],
    label: (v: T) => React.ReactNode,
    cnt: Record<string | number, number>,
    muted?: (v: T) => boolean,
  ) {
    const sel = f[k] as T[];
    return (
      <div className="md-checks">
        {order.map((v) => (
          <label key={String(v)} className="md-check">
            <input type="checkbox" checked={sel.includes(v)} onChange={(e) => toggle(k, order, v, e.target.checked)} />
            <span className={`md-check-l${muted?.(v) ? ' muted' : ''}`}>{label(v)}</span>
            <span className="md-cc">{cnt[v] ?? 0}</span>
          </label>
        ))}
      </div>
    );
  }

  const text = (k: 'emp' | 'empId', ph: string, numeric = false) => (
    <input
      className="md-input"
      type="text"
      inputMode={numeric ? 'numeric' : undefined}
      value={f[k]}
      placeholder={ph}
      onChange={(e) => set(k, e.target.value)}
    />
  );
  const rate = (k: 'rateMin' | 'rateMax') => (
    <span className="md-money">
      <i>$</i>
      <input
        className="md-input"
        type="number"
        min={0}
        step={0.25}
        placeholder="0.00"
        value={f[k]}
        onChange={(e) => set(k, e.target.value)}
      />
    </span>
  );

  let body: React.ReactNode = null;
  if (col === 'emp') {
    body = (
      <>
        <label className="md-field"><span className="md-flab">Name contains</span>{text('emp', 'e.g. Alvarez')}</label>
        <label className="md-field"><span className="md-flab">Employee ID contains</span>{text('empId', 'e.g. 317', true)}</label>
      </>
    );
  } else if (col === 'sup') {
    body = checks<string>('sup', supOptions, (n) => (n === NO_SUPER ? '(none) — no supervisor' : n), counts.sup, (n) => n === NO_SUPER);
  } else if (col === 'days') {
    body = (
      <>
        <div className="md-phelp sh-phelp">Rows with sick time on any checked day.</div>
        {checks<number>('days', [0, 1, 2, 3, 4, 5, 6], (i) => (dates ? `${DOW[i]} ${mdShort(dates[i])}` : DOW[i]), counts.days)}
      </>
    );
  } else if (col === 'rate') {
    body = (
      <>
        {checks<RateState>('rate', RATE_STATES, (r) => r, counts.rate)}
        <div className="md-prow">
          <label className="md-field"><span className="md-flab">Min rate</span>{rate('rateMin')}</label>
          <label className="md-field"><span className="md-flab">Max rate</span>{rate('rateMax')}</label>
        </div>
        <div className="md-phelp">Min / max only match rows with a rate entered.</div>
      </>
    );
  } else if (col === 'ts') {
    body = checks<StageLabel>('ts', STAGES, (t) => t, counts.ts);
  }

  return (
    <MdPopover
      anchor={anchor}
      title={POP_TITLE[col]}
      wide={col === 'sup' || col === 'rate' || col === 'ts'}
      focusKey={col}
      canClear={isColumnFiltered(f, col)}
      onClear={() => setFilters((prev) => clearFilter(prev, col))}
      onClose={onClose}
    >
      {body}
    </MdPopover>
  );
};

/* ====================================================================== *
 * Day chips — wrap to at most two lines; chips that still don't fit fold into +n.
 * ====================================================================== */

const DayChip: React.FC<{ d: ShDay; className?: string }> = ({ d, className }) => (
  <span className={`sh-day${d.pending ? ' p' : ''}${className ? ` ${className}` : ''}`}>
    <span className="dd">{dayLabel(d.date)}</span>
    <span className="sep">·</span>
    <span className="hh">{hShort(d.hours)}</span>
    {d.pending && <span className="sh-ptag">Pending</span>}
  </span>
);

const DayChips: React.FC<{ days: ShDay[]; onTip: (el: HTMLElement | null, days: ShDay[]) => void }> = ({ days, onTip }) => {
  const ref = useRef<HTMLDivElement>(null);
  const [fit, setFit] = useState<ChipFit>({ shown: days.length, solo: false });

  // Measure every chip's natural width (folded ones stay in the DOM, out of flow) and
  // fit. Runs after render and on resize — never on hover or click.
  const measure = useCallback(() => {
    const el = ref.current;
    if (!el) return;
    const chips = Array.from(el.querySelectorAll<HTMLElement>('.sh-day'));
    const more = el.querySelector<HTMLElement>('.sh-more');
    const widths = chips.map((c) => Math.max(c.offsetWidth, c.scrollWidth + (c.offsetWidth - c.clientWidth)));
    const gap = parseFloat(window.getComputedStyle(el).columnGap) || 5;
    const next = fitDayChips(widths, more?.offsetWidth ?? 28, gap, el.clientWidth);
    setFit((prev) => (prev.shown === next.shown && prev.solo === next.solo ? prev : next));
  }, []);

  useLayoutEffect(() => { measure(); }, [measure, days]);

  useEffect(() => {
    const el = ref.current;
    if (!el || typeof ResizeObserver === 'undefined') return undefined;
    const ro = new ResizeObserver(() => measure());
    ro.observe(el);
    // Web fonts can land after the first measure and change every chip's width.
    (document as any).fonts?.ready?.then(() => measure()).catch(() => undefined);
    return () => ro.disconnect();
  }, [measure]);

  const shown = Math.min(fit.shown, days.length);
  const hidden = days.length - shown;
  const over = hidden > 0 || fit.solo;
  const hiddenPending = days.slice(shown).some((d) => d.pending);

  return (
    <div
      ref={ref}
      className={`sh-days${over ? ' over' : ''}${fit.solo ? ' solo' : ''}`}
      onMouseEnter={(e) => { if (over) onTip(e.currentTarget, days); }}
      onMouseLeave={() => onTip(null, days)}
    >
      {days.map((d, i) => <DayChip key={d.date} d={d} className={i < shown ? '' : 'off'} />)}
      <span
        className={`sh-more${hiddenPending ? ' p' : ''}${hidden > 0 ? '' : ' off'}`}
        title={hiddenPending ? 'Includes pending days' : undefined}
      >
        +{Math.max(1, hidden)}
      </span>
    </div>
  );
};

/* ====================================================================== *
 * The page
 * ====================================================================== */

const SickHoursPage: React.FC = () => {
  const { createPopup, payPeriodID, week, setPage, setReviewIntent } = useSiteContext();

  const [data, setData] = useState<SickHoursPayload | null>(null);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState<string | null>(null);

  const [scope, setScope] = useState<Scope>('all');
  const [query, setQuery] = useState('');
  const [filters, setFilters] = useState<ColumnFilters>(emptyFilters);
  const [sort, setSort] = useState<SortState>(DEFAULT_SORT);
  const [pop, setPop] = useState<{ col: FilterCol; anchor: HTMLElement } | null>(null);
  const [tip, setTip] = useState<{ days: ShDay[]; rect: DOMRect } | null>(null);
  const [exporting, setExporting] = useState(false);
  const { toast, toastOn, showToast } = useMdToast();

  const scrollRef = useRef<HTMLDivElement>(null);
  const searchRef = useRef<HTMLInputElement>(null);

  /* ---------------- data ---------------- */

  const loadSeq = useRef(0);
  const load = useCallback(async () => {
    if (!payPeriodID) return; // the shell hasn't picked a week yet — stay on the skeleton
    const seq = ++loadSeq.current;
    setLoading(true);
    setLoadError(null);
    setPop(null);
    setTip(null);
    if (scrollRef.current) scrollRef.current.scrollTop = 0;
    try {
      const payload = await getSickHours(payPeriodID);
      if (seq === loadSeq.current) setData(payload);
    } catch (err) {
      if (seq === loadSeq.current) {
        setData(null);
        setLoadError(sickHoursErrorMessage(err));
      }
    } finally {
      if (seq === loadSeq.current) setLoading(false);
    }
  }, [payPeriodID]);

  useEffect(() => { load(); }, [load]);

  /* ---------------- derived ---------------- */

  const allRows = useMemo(() => (data?.rows ?? []).map(deriveRow), [data]);
  const matched = useMemo(
    () => allRows.filter((r) => matchesSearch(r, query) && matchesFilters(r, filters)),
    [allRows, query, filters],
  );
  const counts = useMemo(() => scopeCounts(matched), [matched]);
  const scopeRows = useMemo(() => allRows.filter((r) => inScope(r, scope)), [allRows, scope]);
  const rows = useMemo(() => sortRows(matched.filter((r) => inScope(r, scope)), sort), [matched, scope, sort]);
  const chips = useMemo(() => filterChips(filters), [filters]);
  const supOptions = useMemo(() => supervisorOptions(allRows), [allRows]);
  const popCounts = useMemo(() => columnCounts(scopeRows), [scopeRows]);
  const sum = useMemo(() => summarize(rows), [rows]);
  const dates = useMemo(() => (data ? weekDates(data.startDate) : null), [data]);

  // The week on screen: the payload's once it matches the pill, else the pill's.
  const fresh = !!data && data.payPeriodID === payPeriodID;
  const wk = fresh && data ? data.weekNumber : week;
  const busy = loading || !payPeriodID;

  /* ---------------- popover / tooltip ---------------- */

  const closePop = useCallback(() => setPop(null), []);
  usePopoverDismiss(!!pop, closePop);

  const togglePop = (col: FilterCol, btn: HTMLButtonElement) => {
    const th = btn.closest('th') as HTMLElement | null;
    setPop((p) => (p?.col === col ? null : { col, anchor: th ?? btn }));
  };

  const hideTip = useCallback(() => setTip(null), []);
  useHideOnScroll(!!tip, hideTip);
  const onDayTip = useCallback((el: HTMLElement | null, days: ShDay[]) => {
    setTip(el ? { days, rect: el.getBoundingClientRect() } : null);
  }, []);

  /* ---------------- actions ---------------- */

  const changeScope = (s: Scope) => {
    setScope(s);
    if (scrollRef.current) scrollRef.current.scrollTop = 0;
  };

  const clearAll = (withSearch: boolean) => {
    setFilters(emptyFilters());
    if (withSearch) setQuery('');
  };

  const removeChip = (key: ChipKey) => setFilters((f) => clearFilter(f, key));

  // Same hand-off the header employee search and Home make: the board consumes the
  // intent once its buckets load and opens the employee's console for the context week.
  const openRow = (x: ShRow) => {
    if (!x.openable) return;
    setPop(null);
    setReviewIntent({
      bucket: bucketForStage(x.src.timesheet?.stage),
      employeeID: x.id,
      altEmployeeID: x.empID,
      name: splitName(x.name),
    });
    setPage(Page.TimesheetReview);
  };

  const doExport = async () => {
    if (!rows.length || exporting || busy || !data) return;
    const n = rows.length;
    setExporting(true);
    try {
      const { blob, fileName } = await exportSickHours(
        { payPeriodID: data.payPeriodID, employeeIDs: rows.map((r) => r.id), suffix: exportSuffix(scope) },
        exportFileName(data.weekNumber, data.startDate, scope),
      );
      downloadBlob(blob, fileName);
      showToast(<><CheckCircle2 /><span><b>{fileName}</b> downloaded · {plural(n, 'row')}</span></>);
    } catch (err: any) {
      createPopup('Export failed', err?.message || 'Could not export sick hours.');
    } finally {
      setExporting(false);
    }
  };

  const onTableScroll = (e: React.UIEvent<HTMLDivElement>) => {
    // Shadow on the pinned Employee column once scrolled sideways (paint only).
    e.currentTarget.classList.toggle('scrolled-x', e.currentTarget.scrollLeft > 0);
  };

  /* ---------------- render pieces ---------------- */

  const th = (c: ColDef, rowSpan?: number) => {
    const key = c.key as SortKey;
    const filterCol = c.filter;
    return (
      <MdTh
        key={key}
        label={c.label}
        fullLabel={c.full}
        num={c.num}
        rowSpan={rowSpan}
        className={c.grp ? 'sh-sub' : undefined}
        sorted={sort.key === key}
        dir={sort.dir}
        filtered={filterCol ? isColumnFiltered(filters, filterCol) : false}
        popOpen={!!filterCol && pop?.col === filterCol}
        onSort={() => setSort((s) => nextSort(s, key))}
        onFilter={filterCol ? (btn) => togglePop(filterCol, btn) : undefined}
      />
    );
  };

  const headRow1: React.ReactNode[] = [];
  const headRow2: React.ReactNode[] = [];
  COLS.forEach((c) => {
    if (c.key === 'go') {
      headRow1.push(<th key="go" rowSpan={2} className="sh-th-go"><span className="md-sr">Open timesheet</span></th>);
    } else if (c.grp) {
      if (c.key === 'appr') headRow1.push(<th key="grp" colSpan={3} className="sh-grp"><span>Sick hours</span></th>);
      headRow2.push(th(c));
    } else {
      headRow1.push(th(c, 2));
    }
  });

  const row = (x: ShRow) => {
    let rateCell: React.ReactNode;
    if (x.rateState === 'Entered') rateCell = <span className="mono">{money(x.rate ?? 0)}</span>;
    else if (x.rateState === 'Needs rate') rateCell = <span className="sh-need"><AlertTriangle />Needs rate</span>;
    else rateCell = <div className="md-cell2 r"><span className="dash">—</span><span className="sub">Pending only</span></div>;

    const open = x.openable;
    return (
      <tr
        key={x.id}
        className={`sh-row${open ? '' : ' nots'}`}
        tabIndex={open ? 0 : undefined}
        title={open ? `Open ${x.name}'s timesheet` : `No timesheet yet for week ${wk}`}
        onClick={open ? () => openRow(x) : undefined}
        onKeyDown={open ? (e) => { if (e.key === 'Enter') openRow(x); } : undefined}
      >
        <td>
          <div className="md-emp">
            <span className="nm">{x.name || '—'}</span>
            <span className="sub mono">#{x.empID}</span>
          </div>
        </td>
        <td>
          <div className="md-cell2 sh-sup">
            {x.sup ? <span className="nm" title={x.sup}>{x.sup}</span> : <span className="dash">—</span>}
            {x.supFallback && <span className="sub">Employee&apos;s supervisor</span>}
          </div>
        </td>
        <td className="sh-dayscell"><DayChips days={x.days} onTip={onDayTip} /></td>
        <td className="num">{x.appr ? <span className="mono">{hrs(x.appr)}</span> : <span className="dash">—</span>}</td>
        <td className="num">
          {x.pend
            ? <span className="sh-pend mono" title="Pending sick request — not yet approved">{hrs(x.pend)}</span>
            : <span className="dash">—</span>}
        </td>
        <td className="num"><span className="mono sh-tot">{hrs(x.total)}</span></td>
        <td className="num">{rateCell}</td>
        <td className="sh-tscell">
          <div className="md-cell2">
            <span className={`sh-ts ${STAGE_TONE[x.stage]}`}><i />{x.stage}</span>
            {!x.src.timesheet && <span className="sub">No timesheet yet</span>}
          </div>
        </td>
        <td className="sh-go">{open && <ChevronRight />}</td>
      </tr>
    );
  };

  const skeleton = () => Array.from({ length: SKELETON_ROWS }, (_, i) => (
    <tr key={`sk${i}`} className="sh-skrow">
      {SKELETON_W.map(([w, sub], j) => (
        <td key={j} className={j >= 3 && j <= 6 ? 'num' : undefined}>
          <span className="sh-sk" style={{ width: `${w + ((i * 7 + j * 13) % 20)}%` }} />
          {sub > 0 && <span className="sh-sk sm" style={{ width: `${sub}%` }} />}
        </td>
      ))}
      <td />
    </tr>
  ));

  const emptyBody = () => {
    const filteredOut = chips.length > 0 || !!query.trim();
    let content: React.ReactNode;
    if (loadError) {
      content = (
        <div className="md-empty">
          <div className="eic"><AlertCircle /></div>
          <div className="et">Couldn&apos;t load sick hours</div>
          <p>{loadError}</p>
          <button type="button" className="md-btn sm" onClick={() => load()}><RefreshCw />Retry</button>
        </div>
      );
    } else if (!allRows.length) {
      content = (
        <div className="md-empty">
          <div className="eic"><CalendarCheck /></div>
          <div className="et">No sick time requested for week {wk}</div>
          <p>
            Approved and pending sick Time Off Requests with hours in
            {data ? ` ${fmtWeekRange(data.startDate, data.endDate)}` : ' this week'} show up here.
            Nothing needs a sick rate this week.
          </p>
        </div>
      );
    } else if (filteredOut) {
      const where = scope === 'all' ? `week ${wk}` : SCOPES.find((s) => s.key === scope)?.label;
      content = (
        <div className="md-empty">
          <div className="eic"><Filter /></div>
          <div className="et">No employees match these filters</div>
          <p>
            Nothing in {where} matches{query.trim() ? ` “${query.trim()}”` : ''}
            {chips.length ? ` with ${plural(chips.length, 'column filter')}` : ''}.
          </p>
          <button type="button" className="md-btn sm" onClick={() => clearAll(true)}><X />Clear filters</button>
        </div>
      );
    } else if (scope === 'needs') {
      content = (
        <div className="md-empty">
          <div className="eic"><CheckCircle2 /></div>
          <div className="et">Every sick rate is entered for week {wk}</div>
          <p>Rows with approved sick hours and no rate show up here.</p>
          <button type="button" className="md-btn sm" onClick={() => changeScope('all')}>View all employees</button>
        </div>
      );
    } else {
      content = (
        <div className="md-empty">
          <div className="eic"><CheckCircle2 /></div>
          <div className="et">No pending sick requests in week {wk}</div>
          <p>All sick time this week is approved.</p>
          <button type="button" className="md-btn sm" onClick={() => changeScope('all')}>View all employees</button>
        </div>
      );
    }
    return <tr className="md-empty-row"><td colSpan={COLS.length}>{content}</td></tr>;
  };

  const n = rows.length;
  const scopeTotal = scopeRows.length;
  const sk = <span className="sh-sk w48" />;

  let body: React.ReactNode;
  if (busy) body = skeleton();
  else if (n) body = rows.map(row);
  else body = emptyBody();

  const sortedBy = sort.key === 'sup'
    ? <>Sorted by <b>Superintendent</b> {sort.dir === 'asc' ? 'A–Z' : 'Z–A'}, then <b>Employee</b></>
    : <>Sorted by <b>{SORT_LABEL[sort.key]}</b> {sort.dir === 'asc' ? 'ascending' : 'descending'}, then Superintendent</>;

  const needsOn = !busy && sum.needsRate > 0;

  return (
    <div className="md-page sh-page">
      <div className="md-head">
        <div className="ph-ic"><Thermometer /></div>
        <div className="grow">
          <h1>Sick Hours</h1>
          <div className="ph-sub">
            Everyone with approved or pending sick time off in the selected week. Enter each sick rate on the
            employee&apos;s timesheet; the week stays in Payroll Pending until it&apos;s set.
          </div>
        </div>
        <div className="ph-actions">
          <button type="button" className="md-btn md-export" onClick={doExport} disabled={busy || !n || exporting}>
            {exporting
              ? <><span className="md-spin" /><span>Exporting…</span></>
              : <><FileSpreadsheet /><span>{busy ? 'Export to Excel' : `Export ${plural(n, 'row')}`}</span></>}
          </button>
        </div>
      </div>

      <div className="sh-sum">
        <div className="sh-stat"><span className="sl">Employees</span><span className="sv">{busy ? sk : sum.employees}</span></div>
        <div className="sh-stat"><span className="sl">Approved hrs</span><span className="sv">{busy ? sk : hrs(sum.approved)}</span></div>
        <div className="sh-stat">
          <span className="sl">Pending hrs</span>
          <span className={`sv${!busy && sum.pending ? ' amber' : ''}`}>{busy ? sk : sum.pending ? hrs(sum.pending) : '—'}</span>
        </div>
        <button
          type="button"
          className={`sh-stat sh-stat-btn${needsOn ? ' attn' : ''}`}
          onClick={() => changeScope('needs')}
          disabled={busy || !sum.needsRate || scope === 'needs'}
          title="Show only rows that need a rate"
        >
          <span className="sl">Needs rate</span>
          <span className="sv">{busy ? sk : sum.needsRate ? <><AlertTriangle />{sum.needsRate}</> : '0'}</span>
        </button>
        <div className="sh-sum-cap">
          {busy ? 'Loading week…' : `Totals for the ${plural(n, 'row')} shown · week ${wk}`}
        </div>
      </div>

      <div className="md-card">
        <div className="md-bar">
          <div className="md-seg" role="radiogroup" aria-label="Quick filter">
            {SCOPES.map((s) => (
              <button
                key={s.key}
                type="button"
                role="radio"
                aria-checked={scope === s.key}
                className={scope === s.key ? 'on' : ''}
                onClick={() => changeScope(s.key)}
              >
                {s.label}
                <span className={`md-cnt${s.key === 'needs' && counts.needs > 0 && !busy ? ' sh-cnt-warn' : ''}`}>
                  {busy ? '–' : counts[s.key]}
                </span>
              </button>
            ))}
          </div>
          <div className="md-search">
            <Search className="md-search-ic" />
            <input
              ref={searchRef}
              type="text"
              value={query}
              placeholder="Search name, ID or superintendent…"
              aria-label="Search"
              onChange={(e) => setQuery(e.target.value)}
            />
            <button
              type="button"
              className="md-qx"
              aria-label="Clear search"
              style={{ visibility: query ? 'visible' : 'hidden' }}
              onClick={() => { setQuery(''); searchRef.current?.focus(); }}
            >
              <X />
            </button>
          </div>
        </div>

        <div className="md-chips">
          <div className="md-chips-l">
            {chips.length
              ? (
                <>
                  {chips.map((c) => (
                    <span key={c.key} className="md-chip" title={c.title}>
                      <b>{c.label}</b>{c.value}
                      <button type="button" aria-label={`Remove ${c.label} filter`} onClick={() => removeChip(c.key)}><X /></button>
                    </span>
                  ))}
                  <button type="button" className="md-linkbtn md-clearall" onClick={() => clearAll(false)}>Clear all</button>
                </>
              )
              : <span className="md-hint-row"><Filter />Filter any column from the icon in its header.</span>}
          </div>
          <div className="md-shown" aria-live="polite">
            {busy ? ' ' : n === scopeTotal ? plural(n, 'employee') : <><b>{n}</b> of {scopeTotal}</>}
          </div>
        </div>

        <div className="md-scroll sh-scroll" ref={scrollRef} onScroll={onTableScroll}>
          <table className="md-tbl sh-tbl">
            <colgroup>{COLS.map((c) => <col key={c.key} style={{ width: c.width }} />)}</colgroup>
            <thead>
              <tr className="sh-r1">{headRow1}</tr>
              <tr className="sh-r2">{headRow2}</tr>
            </thead>
            <tbody>{body}</tbody>
          </table>
        </div>

        <div className="md-foot">
          <span>{sortedBy}</span>
          <span className="grow" />
          <span className="md-legend sh-legend">
            <span className="sh-day sm"><span className="dd">Mon</span><span className="hh">8h</span></span>approved
            <span className="sh-day sm p"><span className="dd">Mon</span><span className="hh">8h</span><span className="sh-ptag">Pending</span></span>pending request
            <span className="sh-need sm"><AlertTriangle />Needs rate</span>approved hours, no rate yet
          </span>
        </div>
      </div>

      {pop && (
        <ShFilterPopover
          col={pop.col}
          anchor={pop.anchor}
          filters={filters}
          setFilters={setFilters}
          supOptions={supOptions}
          counts={popCounts}
          dates={dates}
          onClose={closePop}
        />
      )}

      {tip && (
        <MdTip rect={tip.rect} className="sh-tipbox">
          <div className="sh-tip">
            {tip.days.map((d) => (
              <div key={d.date}>
                <span>{dayLabel(d.date)}</span>
                <b>{hShort(d.hours)}</b>
                <em className={d.pending ? 'p' : ''}>{d.pending ? 'Pending' : 'Approved'}</em>
              </div>
            ))}
          </div>
        </MdTip>
      )}

      <div className={`md-toast${toastOn ? ' on' : ''}`} role="status" aria-live="polite">{toast}</div>
    </div>
  );
};

export default SickHoursPage;
